// ============================================================================
// REGISTRATION TYPE HOOKS
// ============================================================================
// React hooks for the RegistrationType master.
//
// Pattern follows useClientType.ts:
// - useRegistrationTypes(params)   → list with pagination, refetch
// - useRegistrationType(id)        → single record by id
// - useCreateRegistrationType()    → create mutation
// - useUpdateRegistrationType()    → update mutation
// - useDeleteRegistrationType()    → delete mutation
//
// Note: useRegistrationTypes serializes params via JSON.stringify in the dep
// array to avoid infinite re-render when callers pass an inline object.

import { useState, useEffect, useCallback, useMemo } from 'react';
import { registrationTypeService } from 'api/services/registrationTypeService';
import type {
  ID,
  PaginatedResponse,
  RegistrationType,
  CreateRegistrationTypeRequest,
  UpdateRegistrationTypeRequest,
  RegistrationTypeSearchRequest,
} from 'api/types';

const EMPTY_PAGINATION = {
  currentPage: 1,
  pageSize: 25,
  totalPages: 0,
  totalItems: 0,
  hasNextPage: false,
  hasPreviousPage: false,
};

/**
 * Hook to fetch a paginated list of registration types.
 * Refetches automatically when `params` changes.
 */
export const useRegistrationTypes = (params?: RegistrationTypeSearchRequest) => {
  const [data, setData] = useState<RegistrationType[]>([]);
  const [pagination, setPagination] = useState<PaginatedResponse<RegistrationType>['pagination']>(EMPTY_PAGINATION);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  // Serialize params so a new object reference each render doesn't re-fire the effect
  const paramsKey = useMemo(() => JSON.stringify(params ?? {}), [params]);

  const fetchRegistrationTypes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await registrationTypeService.getAll(params);
      setData(response.data);
      setPagination(response.pagination);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  useEffect(() => {
    fetchRegistrationTypes();
  }, [fetchRegistrationTypes]);

  return {
    data,
    pagination,
    loading,
    error,
    refetch: fetchRegistrationTypes,
  };
};

/**
 * Hook to fetch a single registration type by id.
 */
export const useRegistrationType = (id: ID | null | undefined) => {
  const [data, setData] = useState<RegistrationType | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchRegistrationType = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await registrationTypeService.getById(id);
      setData(response.data);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRegistrationType();
  }, [fetchRegistrationType]);

  return { data, loading, error, refetch: fetchRegistrationType };
};

/**
 * Hook to create a new registration type.
 */
export const useCreateRegistrationType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const createRegistrationType = async (
    data: CreateRegistrationTypeRequest,
    options?: {
      onSuccess?: (item: RegistrationType) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await registrationTypeService.create(data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { createRegistrationType, loading, error, success };
};

/**
 * Hook to update a registration type. Always pass the latest rowVersion.
 */
export const useUpdateRegistrationType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const updateRegistrationType = async (
    id: ID,
    data: UpdateRegistrationTypeRequest,
    options?: {
      onSuccess?: (item: RegistrationType) => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await registrationTypeService.update(id, data);
      setSuccess(true);
      options?.onSuccess?.(response.data);
      return response.data;
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { updateRegistrationType, loading, error, success };
};

/**
 * Hook to delete a registration type (soft-delete on the server).
 * The server refuses with 409 when client registrations still reference it.
 */
export const useDeleteRegistrationType = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [success, setSuccess] = useState(false);

  const deleteRegistrationType = async (
    id: ID,
    options?: {
      onSuccess?: () => void;
      onError?: (error: Error) => void;
    }
  ) => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      await registrationTypeService.delete(id);
      setSuccess(true);
      options?.onSuccess?.();
    } catch (err) {
      const e = err as Error;
      setError(e);
      options?.onError?.(e);
      throw e;
    } finally {
      setLoading(false);
    }
  };

  return { deleteRegistrationType, loading, error, success };
};
